(() => {
  const STEPS = ['name', 'stage', 'weight', 'food', 'sensitivities', 'use'];
  const OPTIONS = {
    stage: ['puppy', 'adult', 'senior', 'unsure'],
    food: ['dry', 'wet', 'home', 'raw', 'mixed', 'unsure'],
    sensitivities: ['wheat', 'soy', 'corn', 'pulses', 'meat', 'dairy', 'none', 'unsure'],
    use: ['alongside', 'occasional', 'meal', 'exploring']
  };
  const SINGLE_CHOICE = ['stage', 'food', 'use'];
  const ALLERGENS = ['wheat', 'soy', 'corn', 'pulses', 'meat', 'dairy'];
  const ALLERGEN_PATTERNS = {
    wheat: /wheat/i,
    soy: /\bsoy/i,
    corn: /\bcorn\b|maize/i,
    pulses: /chickpea|mung|\bpeas?\b|lentil|\bdal\b|\bgram\b/i,
    meat: /chicken|meat|beef|mutton|lamb|pork|fish|\begg/i,
    dairy: /milk|dairy|whey|casein|cheese|curd|paneer|lactose|butter|ghee/i
  };
  const WEIGHT_MIN = 1;
  const WEIGHT_MAX = 90;

  function normalizeSensitivities(value) {
    const values = Array.isArray(value) ? [...new Set(value)].filter(item => OPTIONS.sensitivities.includes(item)) : [];
    const specific = values.filter(item => !['none', 'unsure'].includes(item));
    if (specific.length) return specific;
    if (values.includes('unsure')) return ['unsure'];
    return values.includes('none') ? ['none'] : [];
  }

  function normalizeWeight(value) {
    if (value === 'unsure') return 'unsure';
    if (value === '' || value === null || value === undefined || typeof value === 'boolean') return '';
    const number = Math.round(Number(value));
    if (!Number.isFinite(number) || number < WEIGHT_MIN || number > WEIGHT_MAX) return '';
    return number;
  }

  function sanitizeAnswers(input = {}) {
    const source = input && typeof input === 'object' ? input : {};
    const answers = {
      name: typeof source.name === 'string' ? source.name.replace(/[\u0000-\u001f\u007f]/g, '').trim().slice(0, 40) : '',
      weight: normalizeWeight(source.weight),
      sensitivities: normalizeSensitivities(source.sensitivities)
    };
    for (const key of SINGLE_CHOICE) {
      answers[key] = OPTIONS[key].includes(source[key]) ? source[key] : '';
    }
    return answers;
  }

  function isStepValid(answers, index) {
    const key = STEPS[index];
    if (key === 'name') return answers.name.length <= 40;
    if (key === 'weight') return answers.weight !== '';
    if (key === 'sensitivities') return answers.sensitivities.length > 0;
    return Boolean(key && OPTIONS[key].includes(answers[key]));
  }

  function isComplete(answers) {
    return STEPS.every((_, index) => isStepValid(answers, index));
  }

  function initialState() {
    return { answers: sanitizeAnswers(), step: 0, editing: false, error: false };
  }

  function restoreState(input) {
    if (!input || input.version !== 2) return initialState();
    const answers = sanitizeAnswers(input.answers);
    const firstMissing = STEPS.findIndex((_, index) => !isStepValid(answers, index));
    const lastAllowed = firstMissing === -1 ? STEPS.length : firstMissing;
    const requested = Number.isInteger(input.step) ? input.step : 0;
    const step = Math.max(0, Math.min(requested, lastAllowed));
    return { answers, step, editing: Boolean(input.editing && isComplete(answers) && step < STEPS.length), error: false };
  }

  function reduceState(state, action) {
    switch (action.type) {
      case 'answer':
        if (!STEPS.includes(action.key)) return state;
        return { ...state, answers: sanitizeAnswers({ ...state.answers, [action.key]: action.value }), error: false };
      case 'next':
        if (state.step >= STEPS.length) return state;
        if (!isStepValid(state.answers, state.step)) return { ...state, error: true };
        return { ...state, step: state.editing && isComplete(state.answers) ? STEPS.length : Math.min(state.step + 1, STEPS.length), editing: false, error: false };
      case 'back':
        return { ...state, step: Math.max(0, state.step - 1), error: false };
      case 'edit':
        return { ...state, step: Number.isInteger(action.step) ? Math.max(0, Math.min(action.step, STEPS.length - 1)) : 0, editing: Number.isInteger(action.step) && isComplete(state.answers), error: false };
      case 'reset':
        return initialState();
      default:
        return state;
    }
  }

  function parseBand(label) {
    const text = String(label || '').trim();
    const plus = text.match(/^(\d+(?:\.\d+)?)\s*\+$/);
    if (plus) return { min: Number(plus[1]), max: Infinity };
    const range = text.match(/^(\d+(?:\.\d+)?)\s*-\s*(\d+(?:\.\d+)?)$/);
    if (range) return { min: Number(range[1]), max: Number(range[2]) };
    return null;
  }

  function parseFeedingTable(rows) {
    if (!Array.isArray(rows)) return [];
    return rows.map(row => {
      const label = row && (row.band ?? row.key);
      const band = parseBand(label);
      const grams = Number(row && row.grams);
      return band && band.min > 0 && band.max >= band.min && Number.isFinite(grams) && grams > 0 ? { key: String(label).trim(), min: band.min, max: band.max, grams } : null;
    }).filter(Boolean);
  }

  function bandFor(weight, table) {
    if (typeof weight !== 'number') return null;
    const rows = parseFeedingTable(table);
    const exact = rows.find(row => weight >= row.min && weight <= row.max);
    if (exact) return exact;
    // Whole kilograms between two printed rows (for example 5.5 rounds to 6) use the next row up.
    return rows.find(row => weight < row.min) || null;
  }

  function weightTag(weight, table) {
    if (weight === 'unsure') return 'unsure';
    const band = bandFor(weight, table);
    if (!band) return '';
    return band.max === Infinity ? band.min + '_plus' : band.min + '_' + band.max;
  }

  function checkIngredients(sensitivities, ingredients) {
    const list = Array.isArray(ingredients) ? ingredients.map(item => String(item).trim()).filter(Boolean) : [];
    const listed = [];
    const clear = [];
    normalizeSensitivities(sensitivities).filter(item => ALLERGENS.includes(item)).forEach(key => {
      const matches = list.filter(item => ALLERGEN_PATTERNS[key].test(item));
      if (matches.length) listed.push({ key, matches });
      else clear.push(key);
    });
    return { listed, clear };
  }

  function resultFor(answers, ingredients) {
    const clean = sanitizeAnswers(answers);
    const { listed, clear } = checkIngredients(clean.sensitivities, ingredients);
    const notes = [];
    if (clean.sensitivities.includes('unsure')) notes.push('sensitivities_unsure');
    if (clean.stage === 'puppy') notes.push('puppy');
    if (clean.stage === 'senior') notes.push('senior');
    if (clean.stage === 'unsure') notes.push('stage_unsure');
    if (clean.weight === 'unsure') notes.push('weight_unsure');
    if (clean.use === 'meal') notes.push('meal');
    const verdict = listed.length ? 'avoid' : (notes.length || clear.length) ? 'check' : 'good';
    return { verdict, listed, clear, notes };
  }

  function groupForStep(step) { return step >= STEPS.length ? 2 : step >= 3 ? 1 : 0; }

  function leadTagsFor(answers, table) {
    const clean = sanitizeAnswers(answers);
    const tags = ['woofly_quiz', 'newsletter'];
    if (clean.stage) tags.push('quiz_stage_' + clean.stage);
    const weight = weightTag(clean.weight, table);
    if (weight) tags.push('quiz_weight_' + weight);
    for (const key of ['food', 'use']) {
      if (clean[key]) tags.push('quiz_' + key + '_' + clean[key]);
    }
    clean.sensitivities.forEach(value => tags.push('quiz_sensitivity_' + value));
    return tags.join(',');
  }

  function fill(template, values) {
    return String(template || '').replace(/\[(\w+)\]/g, (match, key) => (key in values ? values[key] : match));
  }

  const core = { STEPS, OPTIONS, ALLERGENS, normalizeSensitivities, normalizeWeight, sanitizeAnswers, isStepValid, isComplete, initialState, restoreState, reduceState, parseBand, parseFeedingTable, bandFor, weightTag, checkIngredients, resultFor, groupForStep, leadTagsFor, fill };
  if (typeof module === 'object' && module.exports) module.exports = core;
  if (typeof window === 'undefined' || !window.customElements || customElements.get('woofly-quiz')) return;

  const reduceMotion = () => window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  customElements.define('woofly-quiz', class extends HTMLElement {
    connectedCallback() {
      this.abort?.abort();
      this.abort = new AbortController();
      const options = { signal: this.abort.signal };
      this.copy = JSON.parse(this.querySelector('[data-quiz-copy]').textContent);
      this.data = JSON.parse(this.querySelector('[data-quiz-data]').textContent);
      this.table = parseFeedingTable(this.data.feeding);
      this.form = this.querySelector('[data-quiz-form]');
      this.fields = [...this.querySelectorAll('[data-quiz-step]')];
      this.inputs = [...this.querySelectorAll('[data-answer]')];
      this.error = this.querySelector('[data-quiz-error]');
      this.next = this.querySelector('[data-quiz-next]');
      this.back = this.querySelector('[data-quiz-back]');
      this.results = this.querySelector('[data-quiz-results]');
      this.resultTitle = this.querySelector('[data-result-title]');
      this.stepCount = this.querySelector('[data-step-count]');
      this.range = this.querySelector('[data-weight-range]');
      this.weightInput = this.querySelector('[data-weight-input]');
      this.weightUnsure = this.querySelector('[data-weight-unsure]');
      this.state = initialState();
      this.readSavedState();
      this.restoreInputs();
      this.leadStatus = this.querySelector('[data-quiz-lead-status]');
      if (this.leadStatus) this.state.step = STEPS.length;

      this.form.addEventListener('submit', event => {
        event.preventDefault();
        this.cancelAdvance();
        this.dispatch({ type: 'next' });
      }, options);
      this.form.addEventListener('pointerdown', event => {
        this.pointerChoice = Boolean(event.target.closest('.wf-quiz__choice'));
      }, options);
      this.form.addEventListener('keydown', () => { this.pointerChoice = false; }, options);
      this.form.addEventListener('input', event => {
        const target = event.target;
        if (target === this.range || target === this.weightInput) this.weightChanged(target.value);
        else if (target.dataset.answer === 'name') this.answerChanged(target);
      }, options);
      this.form.addEventListener('change', event => {
        const target = event.target;
        if (target === this.weightUnsure) this.weightChanged(target.checked ? 'unsure' : (this.range ? this.range.value : ''));
        else if (target === this.range || target === this.weightInput) this.weightChanged(target.value);
        else if (target.dataset.answer && target.dataset.answer !== 'name') this.answerChanged(target);
      }, options);
      this.back.addEventListener('click', () => { this.cancelAdvance(); this.dispatch({ type: 'back' }); }, options);
      this.querySelector('[data-quiz-reset]').addEventListener('click', () => { this.cancelAdvance(); this.dispatch({ type: 'reset' }); }, options);
      this.querySelector('[data-quiz-edit]').addEventListener('click', () => this.dispatch({ type: 'edit' }), options);
      this.querySelectorAll('[data-quiz-edit-step]').forEach(button => {
        button.addEventListener('click', () => this.dispatch({ type: 'edit', step: Number(button.dataset.quizEditStep) }), options);
      });
      this.render(false);
      this.querySelector('[data-quiz-fallback]').hidden = true;
      this.querySelector('[data-quiz-experience]').hidden = false;
      if (this.leadStatus) this.leadStatus.focus({ preventScroll: false });
    }

    readSavedState() {
      try {
        const raw = window.sessionStorage.getItem(this.dataset.storageKey);
        if (!raw) return;
        this.state = restoreState(JSON.parse(raw));
        const meaningful = this.state.step > 0 || this.state.answers.name || this.state.answers.stage;
        this.querySelector('[data-quiz-resume]').hidden = !meaningful;
      } catch {
        // The quiz still works when browser storage is unavailable or corrupt.
      }
    }

    saveState() {
      try {
        const { answers, step, editing } = this.state;
        window.sessionStorage.setItem(this.dataset.storageKey, JSON.stringify({ version: 2, answers, step, editing }));
      } catch {
        // Saving locally is optional; it never blocks the quiz.
      }
    }

    restoreInputs() {
      this.inputs.forEach(input => {
        const key = input.dataset.answer;
        if (key === 'name') input.value = this.state.answers.name;
        else if (key === 'sensitivities') input.checked = this.state.answers.sensitivities.includes(input.value);
        else if (key !== 'weight') input.checked = input.value === this.state.answers[key];
      });
      this.syncWeightInputs();
      this.updateLabelCheck();
    }

    displayName() {
      return this.state.answers.name || this.copy.dogFallback;
    }

    weightChanged(value) {
      const unsure = value === 'unsure';
      if (!unsure && this.weightUnsure) this.weightUnsure.checked = false;
      this.state = reduceState(this.state, { type: 'answer', key: 'weight', value });
      this.error.hidden = true;
      this.fields.forEach(field => field.removeAttribute('aria-invalid'));
      this.syncWeightInputs();
      this.saveState();
      this.renderProfile();
    }

    syncWeightInputs() {
      const weight = this.state.answers.weight;
      const known = typeof weight === 'number';
      if (this.range) {
        if (known) this.range.value = String(Math.min(weight, Number(this.range.max)));
        this.range.classList.toggle('is-unset', !known);
        this.range.style.setProperty('--fill', ((Number(this.range.value) - Number(this.range.min)) / (Number(this.range.max) - Number(this.range.min)) * 100) + '%');
        this.range.setAttribute('aria-valuetext', known ? fill(this.copy.kilograms, { kg: weight }) : this.copy.weightUnset);
      }
      if (this.weightInput && document.activeElement !== this.weightInput) this.weightInput.value = known ? String(weight) : '';
      if (this.weightUnsure) this.weightUnsure.checked = weight === 'unsure';
      const dog = this.querySelector('[data-weight-dog]');
      if (dog) {
        const kg = known ? weight : Number(this.range ? this.range.value : 15);
        const scale = 0.5 + Math.min(kg, 70) / 70 * 0.62;
        dog.style.setProperty('--dog-scale', scale.toFixed(3));
        dog.classList.toggle('is-muted', weight === 'unsure');
      }
      const band = this.querySelector('[data-weight-band]');
      if (band) {
        const row = known && this.state.answers.stage === 'adult' ? bandFor(weight, this.table) : null;
        band.textContent = weight === 'unsure' ? this.copy.weightUnsureNote : row ? fill(this.copy.weightBand, { band: this.bandLabel(row), grams: row.grams }) : this.copy.weightPrompt;
      }
    }

    bandLabel(row) {
      return row.max === Infinity ? fill(this.copy.bandOver, { min: row.min }) : fill(this.copy.bandRange, { min: row.min, max: row.max });
    }

    answerChanged(input) {
      const key = input.dataset.answer;
      let value = input.value;
      if (key === 'sensitivities') {
        const group = this.inputs.filter(item => item.dataset.answer === key);
        if (input.checked) {
          const exclusive = ['none', 'unsure'].includes(input.value);
          group.forEach(item => {
            if (item !== input && (exclusive || ['none', 'unsure'].includes(item.value))) item.checked = false;
          });
        }
        value = group.filter(item => item.checked).map(item => item.value);
      }
      this.state = reduceState(this.state, { type: 'answer', key, value });
      this.error.hidden = true;
      this.fields.forEach(field => field.removeAttribute('aria-invalid'));
      this.saveState();
      if (key === 'name') this.renderNames();
      if (key === 'sensitivities') this.updateLabelCheck();
      this.renderProfile();
      if (SINGLE_CHOICE.includes(key) && this.pointerChoice && input.checked) this.scheduleAdvance();
      this.pointerChoice = false;
    }

    scheduleAdvance() {
      this.cancelAdvance();
      const step = this.state.step;
      this.advanceTimer = window.setTimeout(() => {
        if (this.state.step === step) this.dispatch({ type: 'next' });
      }, reduceMotion() ? 150 : 420);
    }

    cancelAdvance() {
      if (this.advanceTimer) window.clearTimeout(this.advanceTimer);
      this.advanceTimer = null;
    }

    updateLabelCheck() {
      const box = this.querySelector('[data-label-check]');
      if (!box) return;
      const selected = this.state.answers.sensitivities;
      const { listed, clear } = checkIngredients(selected, this.data.ingredients);
      const lines = [];
      listed.forEach(item => lines.push({ type: 'listed', text: fill(this.copy.labelListed, { item: this.copy.allergens[item.key], matches: item.matches.join(this.copy.separator) }) }));
      clear.forEach(key => lines.push({ type: 'clear', text: fill(this.copy.labelClear, { item: this.copy.allergensLower[key] }) }));
      if (selected.includes('none')) lines.push({ type: 'clear', text: this.copy.labelNone });
      if (selected.includes('unsure')) lines.push({ type: 'unsure', text: this.copy.labelUnsure });
      box.replaceChildren(...lines.map(line => {
        const item = document.createElement('li');
        item.className = 'wf-quiz__check wf-quiz__check--' + line.type;
        item.textContent = line.text;
        return item;
      }));
      box.hidden = lines.length === 0;
    }

    dispatch(action) {
      this.state = reduceState(this.state, action);
      if (action.type === 'reset') {
        try { window.sessionStorage.removeItem(this.dataset.storageKey); } catch { /* Storage is optional. */ }
        this.restoreInputs();
        this.querySelector('[data-quiz-resume]').hidden = true;
        this.querySelector('[data-quiz-announcement]').textContent = this.copy.resetDone;
      } else {
        this.saveState();
      }
      this.render(true);
    }

    renderNames() {
      const name = this.displayName();
      this.querySelectorAll('[data-name-template]').forEach(node => {
        node.textContent = fill(node.dataset.nameTemplate, { name });
      });
    }

    profileItems() {
      const answers = this.state.answers;
      const items = [];
      if (answers.name) items.push({ step: 0, text: answers.name, strong: true });
      if (answers.stage) items.push({ step: 1, text: this.copy.profile.stage[answers.stage] });
      if (typeof answers.weight === 'number') items.push({ step: 2, text: fill(this.copy.kilograms, { kg: answers.weight }) });
      else if (answers.weight === 'unsure') items.push({ step: 2, text: this.copy.profile.weightUnsure });
      if (answers.food) items.push({ step: 3, text: this.copy.profile.food[answers.food] });
      const specific = answers.sensitivities.filter(item => ALLERGENS.includes(item));
      if (specific.length) items.push({ step: 4, text: fill(this.copy.profile.avoids, { items: specific.map(key => this.copy.allergensLower[key]).join(this.copy.separator) }) });
      else if (answers.sensitivities.includes('none')) items.push({ step: 4, text: this.copy.profile.noAllergies });
      if (answers.use) items.push({ step: 5, text: this.copy.profile.use[answers.use] });
      return items;
    }

    renderProfile() {
      const bar = this.querySelector('[data-quiz-profile]');
      if (!bar) return;
      const complete = this.state.step === STEPS.length;
      const items = this.profileItems().filter(item => complete || item.step < this.state.step || (item.step === this.state.step && SINGLE_CHOICE.concat(['weight', 'sensitivities']).includes(STEPS[item.step])));
      const list = bar.querySelector('ul');
      list.replaceChildren(...items.map(item => {
        const li = document.createElement('li');
        li.textContent = item.text;
        if (item.strong) li.classList.add('is-name');
        return li;
      }));
      bar.hidden = complete || items.length === 0;
    }

    render(moveFocus) {
      const complete = this.state.step === STEPS.length;
      this.querySelector('[data-quiz-questions]').hidden = complete;
      this.querySelector('[data-quiz-welcome]').hidden = this.state.step !== 0;
      this.results.hidden = !complete;
      this.renderNames();
      this.syncWeightInputs();
      this.stepCount.textContent = complete ? this.copy.title : fill(this.copy.stepCount, { current: this.state.step + 1, total: STEPS.length });
      const progress = this.querySelector('[data-quiz-progress]');
      progress.value = Math.min(this.state.step + 1, STEPS.length);
      progress.setAttribute('aria-valuetext', this.stepCount.textContent);
      const bar = this.querySelector('[data-quiz-progress-bar]');
      if (bar) bar.style.setProperty('--progress', String(groupForStep(this.state.step) / 2));
      this.querySelectorAll('[data-group-indicator]').forEach((item, index) => {
        const group = groupForStep(this.state.step);
        item.classList.toggle('is-complete', index < group);
        if (index === group) item.setAttribute('aria-current', 'step');
        else item.removeAttribute('aria-current');
      });
      this.fields.forEach((field, index) => {
        field.hidden = complete || index !== this.state.step;
        field.disabled = field.hidden;
        field.removeAttribute('aria-invalid');
      });
      this.back.hidden = this.state.step === 0;
      this.next.firstChild.textContent = this.state.editing ? this.copy.update : this.state.step === STEPS.length - 1 ? this.copy.finish : this.copy.next;
      this.renderProfile();
      this.error.hidden = !this.state.error;
      if (this.state.error) {
        const key = STEPS[this.state.step];
        this.error.textContent = key === 'weight' ? this.copy.weightRequired : this.copy.required;
        const field = this.fields[this.state.step];
        field.setAttribute('aria-invalid', 'true');
        (key === 'weight' ? this.weightInput : field.querySelector('input'))?.focus();
        return;
      }
      if (complete) this.renderResult();
      else {
        // Warm only the next step while the visitor answers the current one.
        this.fields[this.state.step + 1]?.querySelectorAll('img[loading="lazy"]').forEach(image => {
          image.loading = 'eager';
        });
      }
      if (moveFocus) {
        const panel = complete ? this.results : this.fields[this.state.step];
        panel.classList.remove('is-entering');
        void panel.offsetWidth;
        panel.classList.add('is-entering');
        const target = complete ? this.resultTitle : this.fields[this.state.step].querySelector('legend');
        target.focus({ preventScroll: true });
        const bounds = target.getBoundingClientRect();
        if (bounds.top < 140 || bounds.bottom > window.innerHeight - 100) {
          target.scrollIntoView({ block: 'start', behavior: 'instant' });
        }
      }
    }

    renderResult() {
      const answers = this.state.answers;
      const name = this.displayName();
      const result = resultFor(answers, this.data.ingredients);
      const tags = this.querySelector('[data-quiz-lead-tags]');
      if (tags) tags.value = leadTagsFor(answers, this.data.feeding);
      this.resultTitle.textContent = answers.name ? fill(this.copy.namedTitle, { name: answers.name }) : this.copy.title;

      const portrait = this.querySelector('[data-result-portrait]');
      if (portrait) {
        const stage = answers.stage && answers.stage !== 'unsure' ? answers.stage : 'unsure';
        portrait.querySelectorAll('[data-portrait]').forEach(image => { image.hidden = image.dataset.portrait !== stage; });
      }
      const chips = this.querySelector('[data-result-profile]');
      if (chips) chips.replaceChildren(...this.profileItems().filter(item => !item.strong).map(item => {
        const li = document.createElement('li');
        li.textContent = item.text;
        return li;
      }));

      const verdict = this.querySelector('[data-result-verdict]');
      verdict.dataset.verdict = result.verdict;
      verdict.querySelector('[data-verdict-heading]').textContent = fill(this.copy.verdict[result.verdict].heading, { name });
      verdict.querySelector('[data-verdict-text]').textContent = fill(this.copy.verdict[result.verdict].text, { name });
      const notes = verdict.querySelector('[data-verdict-notes]');
      const noteItems = [];
      result.listed.forEach(item => noteItems.push({ type: 'listed', text: fill(this.copy.labelListed, { item: this.copy.allergens[item.key], matches: item.matches.join(this.copy.separator) }) }));
      result.clear.forEach(key => noteItems.push({ type: 'clear', text: fill(this.copy.labelClear, { item: this.copy.allergensLower[key] }) + ' ' + this.copy.crossContact }));
      result.notes.forEach(key => noteItems.push({ type: 'note', text: fill(this.copy.notes[key], { name }) }));
      notes.replaceChildren(...noteItems.map(note => {
        const li = document.createElement('li');
        li.className = 'wf-quiz__check wf-quiz__check--' + note.type;
        li.textContent = note.text;
        return li;
      }));
      notes.hidden = noteItems.length === 0;

      const row = answers.stage === 'adult' && typeof answers.weight === 'number' ? bandFor(answers.weight, this.table) : null;
      this.querySelectorAll('[data-feeding-row]').forEach(tr => {
        const match = Boolean(row && tr.dataset.feedingRow === row.key);
        tr.classList.toggle('is-match', match);
        if (match) tr.setAttribute('aria-current', 'true');
        else tr.removeAttribute('aria-current');
      });
      const amount = this.querySelector('[data-feeding-amount]');
      const amountNote = this.querySelector('[data-feeding-amount-note]');
      if (row) {
        amount.textContent = fill(this.copy.gramsPerDay, { grams: row.grams });
        amountNote.textContent = fill(this.copy.feedingFor, { name, band: this.bandLabel(row) });
      } else {
        amount.textContent = this.copy.feedingNoWeightHeading;
        amountNote.textContent = fill(this.copy.feedingNoWeight, { name });
      }
      amount.closest('[data-feeding-headline]').classList.toggle('has-amount', Boolean(row));
      const useNote = this.querySelector('[data-feeding-use]');
      let useText = answers.use ? fill(this.copy.useNotes[answers.use], { name }) : '';
      useNote.textContent = useText;
      useNote.hidden = !useText;

      const flagged = new Set(result.listed.flatMap(item => item.matches));
      this.querySelectorAll('[data-ingredient]').forEach(chip => {
        chip.classList.toggle('is-flagged', flagged.has(chip.dataset.ingredient));
      });
      const legend = this.querySelector('[data-ingredient-legend]');
      if (legend) legend.hidden = flagged.size === 0;

      this.querySelectorAll('[data-answer-summary]').forEach(item => {
        const key = item.dataset.answerSummary;
        if (key === 'name') { item.textContent = answers.name || this.copy.notProvided; return; }
        if (key === 'weight') { item.textContent = typeof answers.weight === 'number' ? fill(this.copy.kilograms, { kg: answers.weight }) : this.copy.profile.weightUnsure; return; }
        const selected = Array.isArray(answers[key]) ? answers[key] : [answers[key]];
        item.textContent = this.inputs.filter(input => input.dataset.answer === key && selected.includes(input.value))
          .map(input => input.closest('label').querySelector('[data-option-label]').textContent.trim()).join(this.copy.separator);
      });
    }

    disconnectedCallback() {
      this.cancelAdvance();
      this.abort?.abort();
    }
  });
})();
